import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ensureNotificationLoginBaseline, unsubscribePushNotifications } from '../utils/notifications';
import { disconnectSocket } from '../utils/socket';

interface User {
  id: number;
  email: string;
  name?: string;
  profileCompleted: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = user !== null;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setUser({
        id: response.data.user.id,
        email: response.data.user.email,
        name: response.data.user.name,
        profileCompleted: response.data.user.profile_completed,
      });
      ensureNotificationLoginBaseline(Number(response.data.user.id));
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Token is invalid or expired — clean up everything
      localStorage.removeItem('token');
      disconnectSocket();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (token: string) => {
    localStorage.setItem('token', token);
    loadUserProfile();
  };

  const logout = async () => {
    const token = localStorage.getItem('token');

    try {
      // Ask the server to delete the session and boot the server-side socket
      if (token) {
        await api.post('/logout');
      }
    } catch (error) {
      // Server call failed (expired session, network issue, etc.)
      // Local cleanup still runs in finally so it's safe to ignore
      console.warn('Server logout failed, cleaning up locally:', error);
    } finally {
      // Always clean up locally regardless of server response
      await unsubscribePushNotifications().catch(() => {});
      disconnectSocket();
      localStorage.removeItem('token');
      setUser(null);
      navigate('/content');
    }
  };

  const refreshUser = async () => {
    setLoading(true);
    await loadUserProfile();
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
